import React from 'react';
import PropTypes from 'prop-types';
import { compose, mapProps } from '@shakacode/recompose';
import { useHistory, withRouter } from 'react-router-dom';
import { Typography } from '@popmenu/common-ui';

import { FormattedMessage } from 'react-intl';
import { toInt } from '../../utils/numbers';
import userByUnsubscribeMessageTokenQuery from '../../libs/gql/queries/users/userByUnsubscribeMessageTokenQuery.gql';
import updateUserSubscriptionsMutation from '../../libs/gql/mutations/users/updateUserSubscriptionsMutation.gql';

import BasicForm, { CheckBoxGroup, SubmitGroup } from '../shared/forms/BasicForm';
import Loading from '../../shared/Loading';
import Query from '../../shared/Query';

const UnsubscribeContainer = (props) => {
  const history = useHistory();
  let messageToken = '';
  if (props.urlSearchParamsString !== '') {
    const params = new URLSearchParams(props.urlSearchParamsString.search);
    messageToken = params.get('message_token');
  }
  const { unsubscribeToken } = props.match.params;
  const validateInput = (values) => {
    if (values.unsubscribedRestaurantIds.some(value => value)) {
      return false;
    }

    return true;
  };
  return (
    <Query
      query={userByUnsubscribeMessageTokenQuery}
      variables={{ messageToken, unsubscribeToken }}
    >
      {({ data, error, loading }) => {
        if (loading) {
          return <Loading />;
        }

        if (error) {
          return (
            <Typography align="center">
              <FormattedMessage
                defaultMessage="Something went wrong. Please refresh the page and try again."
              />
            </Typography>
          );
        }

        const result = data?.userByUnsubscribeMessageToken;

        if (result?.recipientRemoved) {
          return (
            <Typography align="center">
              {result.restaurantName ? (
                <FormattedMessage
                  defaultMessage="You have stopped receiving email messages from {restaurantName}"
                  values={{ restaurantName: result.restaurantName }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage="You have stopped receiving email messages"
                />
              )}
            </Typography>
          );
        }

        if (!result?.user) {
          return (
            <Typography align="center">
              <FormattedMessage
                defaultMessage="This unsubscribe link is no longer valid."
              />
            </Typography>
          );
        }

        const { user, restaurantId } = result;
        const { followers } = user;

        const restaurantsToUnsubscribe = restaurantId ? followers.filter(follower => follower.restaurant.id === restaurantId) : followers;

        return (
          <React.Fragment>
            <Typography align="center" variant="h5">
              <FormattedMessage
                defaultMessage="Subscriptions"
              />
            </Typography>
            <br />
            <br />
            <Typography>
              <FormattedMessage
                defaultMessage="We're sorry to see you go!"
              />
            </Typography>
            <br />
            <br />
            <Typography>
              <Typography>
                <FormattedMessage
                  defaultMessage="Would you prefer to stop receiving marketing messages?"
                />
              </Typography>
            </Typography>
            <BasicForm
              defaultValues={{
                unsubscribedRestaurantIds: restaurantsToUnsubscribe.map(follower => (
                  follower.isUnsubscribed || follower.restaurant.id === restaurantId ? follower.restaurant.id : null
                )),
              }}
              mutate={{
                mutation: updateUserSubscriptionsMutation,
                onCompleted: () => {
                  history.push(`/unsubscribed/${unsubscribeToken}`);
                },
                toVariables: ({ unsubscribedRestaurantIds, ...variables }) => ({
                  ...variables,
                  messageGroupMessageToken: messageToken,
                  unsubscribedRestaurantIds: (unsubscribedRestaurantIds || []).map(id => toInt(id)),
                  unsubscribeToken,
                }),
              }}
            >
              {({ values }) => (
                <React.Fragment>
                  {restaurantsToUnsubscribe.map((follower, i) => (
                    <CheckBoxGroup
                      data-cy="unsubscribe_restaurant"
                      field={`unsubscribedRestaurantIds[${i}]`}
                      inline
                      key={follower.id}
                      title={`Unsubscribe from ${follower.restaurant.name}`}
                      value={follower.restaurant.id}
                    />
                  ))}
                  <br />
                  <br />
                  <Typography>
                    <FormattedMessage
                      defaultMessage="Note: You will still receive emails about any online orders you place at these restaurants."
                    />
                  </Typography>

                  <SubmitGroup
                    color="secondary"
                    data-tour-id="user-update-subscription-preferences-submit-button"
                    disabled={validateInput(values)}
                    justifyContent="center"
                    offset={0}
                    title={<FormattedMessage defaultMessage="Update Preferences" />}
                  />
                </React.Fragment>
              )}
            </BasicForm>
          </React.Fragment>
        );
      }}
    </Query>
  );
};

UnsubscribeContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      unsubscribeToken: PropTypes.string,
    }),
  }).isRequired,
};

export default compose(
  withRouter,
  mapProps(({ location, ...props }) => ({
    ...props,
    urlSearchParamsString: location,
  })),
)(UnsubscribeContainer);
